import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Pagination,
  Row,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEdit, FaPlus, FaSearch, FaTrash, FaUsers } from 'react-icons/fa';
import moment from 'moment';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import {
  excluirLocalAtendimento,
  listarLocalAtendimento,
} from '~/Services/Service';

import FormLocalAtendimento from './FormLocalAtendimento';
import ModalVincularUsuarios from './ModalVincularUsuarios';

const TAMANHO_PAGINA = 20;

const formatarData = (valor) => {
  if (!valor) return '-';
  const m = moment(valor);
  return m.isValid() ? m.format('DD/MM/YYYY') : '-';
};

const LocalAtendimento = () => {
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtroDescricao, setFiltroDescricao] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState('');
  const [ordenarPor, setOrdenarPor] = useState('Descricao');
  const [ordemAsc, setOrdemAsc] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [vinculando, setVinculando] = useState(null);

  const totalPaginas = Math.max(1, Math.ceil(total / TAMANHO_PAGINA));

  const carregar = useCallback(
    (aborter) => {
      setCarregando(true);
      listarLocalAtendimento({
        descricao: filtroAplicado,
        pagina,
        tamanho_pagina: TAMANHO_PAGINA,
        ordenar_por: ordenarPor,
        ordem_asc: ordemAsc,
      })
        .then((res) => {
          if (aborter && !aborter()) return;
          setItens(Array.isArray(res && res.itens) ? res.itens : []);
          setTotal(res && typeof res.total === 'number' ? res.total : 0);
        })
        .catch((err) => {
          if (aborter && !aborter()) return;
          toast.error(extrairMensagemErro(err));
          setItens([]);
          setTotal(0);
        })
        .finally(() => {
          if (aborter && !aborter()) return;
          setCarregando(false);
        });
    },
    [filtroAplicado, pagina, ordenarPor, ordemAsc]
  );

  useEffect(() => {
    let ativo = true;
    carregar(() => ativo);
    return () => {
      ativo = false;
    };
  }, [carregar]);

  const handleAplicarFiltro = (evento) => {
    if (evento && typeof evento.preventDefault === 'function') {
      evento.preventDefault();
    }
    setPagina(1);
    setFiltroAplicado(filtroDescricao.trim());
  };

  const handleLimparFiltro = () => {
    setFiltroDescricao('');
    setFiltroAplicado('');
    setPagina(1);
  };

  const handleOrdenar = (campo) => {
    if (ordenarPor === campo) {
      setOrdemAsc(!ordemAsc);
    } else {
      setOrdenarPor(campo);
      setOrdemAsc(true);
    }
    setPagina(1);
  };

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    setPagina(pg);
  };

  const handleConfirmarExclusao = (confirmou) => {
    const alvo = excluindo;
    setExcluindo(null);
    if (!confirmou || !alvo) return;

    excluirLocalAtendimento({ codigo: alvo.codigo })
      .then(() => {
        toast.success('Local de Atendimento excluído com sucesso.');
        // UX-001: deletar último item da última página → volta uma página.
        const totalPosExclusao = total - 1;
        const ultimaValida = Math.max(
          1,
          Math.ceil(totalPosExclusao / TAMANHO_PAGINA)
        );
        if (pagina > ultimaValida) {
          setPagina(ultimaValida);
        } else {
          let ativo = true;
          carregar(() => ativo);
        }
      })
      .catch((err) => {
        toast.error(extrairMensagemErro(err));
      });
  };

  const handleAposSalvar = () => {
    setEditando(null);
    let ativo = true;
    carregar(() => ativo);
  };

  const renderSetaOrdem = (campo) => {
    if (ordenarPor !== campo) return '↕';
    return ordemAsc ? '↑' : '↓';
  };

  const renderPaginacao = useMemo(() => {
    if (totalPaginas <= 1) return null;
    const max = 5;
    let start = Math.max(1, pagina - Math.floor(max / 2));
    const end = Math.min(totalPaginas, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    const itensPagina = [];
    for (let i = start; i <= end; i += 1) {
      itensPagina.push(
        <Pagination.Item
          key={`pag-${i}`}
          active={i === pagina}
          onClick={() => handlePagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return (
      <Pagination size="sm" className="mb-0">
        <Pagination.First
          onClick={() => handlePagina(1)}
          disabled={pagina === 1}
        />
        <Pagination.Prev
          onClick={() => handlePagina(pagina - 1)}
          disabled={pagina === 1}
        />
        {start > 1 && <Pagination.Ellipsis disabled />}
        {itensPagina}
        {end < totalPaginas && <Pagination.Ellipsis disabled />}
        <Pagination.Next
          onClick={() => handlePagina(pagina + 1)}
          disabled={pagina === totalPaginas}
        />
        <Pagination.Last
          onClick={() => handlePagina(totalPaginas)}
          disabled={pagina === totalPaginas}
        />
      </Pagination>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, totalPaginas]);

  const inicio = total === 0 ? 0 : (pagina - 1) * TAMANHO_PAGINA + 1;
  const fim = Math.min(pagina * TAMANHO_PAGINA, total);

  return (
    <>
      <Header title="Local de Atendimento" />
      <Container fluid className="py-3">
        <Card className="mb-3">
          <Card.Body>
            <Form onSubmit={handleAplicarFiltro}>
              <Row className="align-items-end">
                <Col xs={12} md={6} className="mb-2 mb-md-0">
                  <Form.Label className="small mb-1">Descrição</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por descrição..."
                    value={filtroDescricao}
                    onChange={(e) => setFiltroDescricao(e.target.value)}
                    maxLength={100}
                  />
                </Col>
                <Col xs={12} md={6} className="d-flex flex-wrap gap-2">
                  <Button type="submit" variant="primary" className="mr-2">
                    <FaSearch className="mr-1" /> Buscar
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleLimparFiltro}
                    className="mr-2"
                  >
                    Limpar
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    onClick={() => setEditando({})}
                  >
                    <FaPlus className="mr-1" /> Novo
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            {carregando ? (
              <Loading />
            ) : (
              <>
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: 'pointer', width: 110 }}
                          onClick={() => handleOrdenar('Codigo')}
                        >
                          Código {renderSetaOrdem('Codigo')}
                        </th>
                        <th
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOrdenar('Descricao')}
                        >
                          Descrição {renderSetaOrdem('Descricao')}
                        </th>
                        <th style={{ width: 160 }}>Data Cadastro</th>
                        <th style={{ width: 180 }} className="text-center">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            Nenhum local de atendimento encontrado.
                          </td>
                        </tr>
                      ) : (
                        itens.map((item) => (
                          <tr key={`local-${item.codigo}`}>
                            <td>{item.codigo}</td>
                            <td>{item.descricao}</td>
                            <td>{formatarData(item.data_cadastro)}</td>
                            <td className="text-center">
                              <Button
                                size="sm"
                                variant="link"
                                aria-label="Editar"
                                title="Editar"
                                onClick={() => setEditando(item)}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="link"
                                aria-label="Vincular usuários"
                                title="Vincular usuários"
                                onClick={() => setVinculando(item)}
                              >
                                <FaUsers />
                              </Button>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-danger"
                                aria-label="Excluir"
                                title="Excluir"
                                onClick={() => setExcluindo(item)}
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                <Row className="align-items-center mt-2">
                  <Col xs={12} md="auto" className="text-muted small">
                    {total === 0
                      ? 'Sem registros para exibir.'
                      : `Mostrando ${inicio} até ${fim} de ${total} registros`}
                  </Col>
                  <Col className="d-flex justify-content-md-end mt-2 mt-md-0">
                    {renderPaginacao}
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {editando !== null && (
        <FormLocalAtendimento
          item={editando}
          onClose={() => setEditando(null)}
          onSaved={handleAposSalvar}
        />
      )}

      <ModalConfirmacao
        show={excluindo !== null}
        onClose={handleConfirmarExclusao}
        title="Excluir Local de Atendimento"
        message={
          excluindo
            ? `Deseja excluir o local "${excluindo.descricao}"?`
            : 'Deseja excluir?'
        }
        variant="danger"
      />

      {vinculando !== null && (
        <ModalVincularUsuarios
          local={vinculando}
          onClose={() => setVinculando(null)}
        />
      )}
    </>
  );
};

export default LocalAtendimento;
